import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { LocalizationPipe, LocalizationService, TimeService, TimezoneService } from '@abp/ng.core';
import { ButtonModule } from 'primeng/button';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { Popover, PopoverModule } from 'primeng/popover';
import { DateRange } from './date-range.model';

@Component({
  selector: 'cx-datepicker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    ButtonModule,
    PopoverModule,
    LocalizationPipe,
  ],
  templateUrl: './cx-datepicker.component.html',
  styleUrls: ['./cx-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CxDatepickerComponent implements OnInit, OnChanges {
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly timeService = inject(TimeService);
  private readonly timezoneService = inject(TimezoneService);

  @Input() minDate: Date = this.getDefaultMinDate();
  @Input() maxDate: Date = new Date();
  @Input() isMaxDateToday = false;
  @Input() defaultRangeDays = 30;
  @Input() showLast90Days = true;
  @Input() maxRangeDays: number | null = null;

  @Input() set initialStartDate(value: Date | null) {
    this.startDate = value ? new Date(value) : null;
    this.syncDraftValues();
    this.updateDisplayRangeLabel();
  }

  @Input() set initialEndDate(value: Date | null) {
    this.endDate = value ? new Date(value) : null;
    this.syncDraftValues();
    this.updateDisplayRangeLabel();
  }

  /**
   * Restores the quick-range selection (7 / 30 / 90 days) alongside `initialStartDate` and
   * `initialEndDate`.
   *
   * Optional and purely cosmetic: the dates alone drive the query, but without this a restored
   * "Last 7 days" would show the right dates with no quick-range button highlighted, which reads as
   * a custom range the user never picked.
   */
  @Input() set initialPeriod(value: number | null) {
    this.selectedPeriod = value ?? null;
    this.activeQuickRangeDays = value ?? null;
  }

  @Input() labels = {
    apply: 'Dashboard::Apply',
  };

  @Output() dateRangeChange = new EventEmitter<DateRange>();

  @ViewChild('popover') popover?: Popover;
  @ViewChild(DatePicker) datePicker?: DatePicker;

  startDate: Date | null = null;
  endDate: Date | null = null;
  draftRangeValue: Date[] = [];
  displayRangeLabel = '';
  effectiveMaxDate: Date = new Date();
  quickRangeLabels = {
    today: '',
    last7: '',
    last30: '',
    last90: '',
  };
  activeQuickRangeDays: number | null = null;
  private firstClickDate: Date | null = null;
  private selectedPeriod: number | null = null;
  private draftPeriod: number | null = null;
  private suppressAutoApplyOnHide = false;
  private displayDateFormatter: Intl.DateTimeFormat = this.createDisplayDateFormatter(
    this.localization.currentLang,
  );

  ngOnInit(): void {
    this.updateLocalizedLabels();
    this.refreshEffectiveMaxDate();
    this.initializeDefaultDatesIfNeeded();
    this.emitCurrentRange();

    this.localization.languageChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(language => {
        this.displayDateFormatter = this.createDisplayDateFormatter(language);
        this.updateLocalizedLabels();
        this.updateDisplayRangeLabel();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isMaxDateToday'] && !changes['maxDate']) {
      return;
    }

    this.refreshEffectiveMaxDate();
  }

  get canApply(): boolean {
    return this.getRangeFromDraft() !== null && !this.isRangeExceedingMax;
  }

  get isRangeExceedingMax(): boolean {
    if (!this.maxRangeDays) {
      return false;
    }

    const range = this.getRangeFromDraft();
    if (!range) {
      return false;
    }

    const diffMs = range.end.getTime() - range.start.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > this.maxRangeDays;
  }

  togglePopover(event: Event): void {
    this.refreshEffectiveMaxDate();
    this.syncDraftValues();
    this.firstClickDate = null;
    this.popover?.toggle(event);
  }

  onQuickRangeClick(days: number): void {
    this.firstClickDate = null;
    this.activeQuickRangeDays = days;
    this.draftPeriod = days;
    const end = new Date(this.effectiveMaxDate);
    end.setHours(23, 59, 0, 0);

    const start = new Date(end);
    start.setDate(start.getDate() - Math.max(days - 1, 0));
    start.setHours(0, 0, 0, 0);

    const minBound = new Date(this.minDate);
    minBound.setHours(0, 0, 0, 0);

    if (minBound > end) {
      this.draftRangeValue = [end, end];
      return;
    }

    if (start < minBound) {
      this.draftRangeValue = [minBound, end];
      return;
    }

    this.draftRangeValue = [start, end];
  }

  onApplyClick(): void {
    if (!this.applyDraftRange()) {
      return;
    }

    this.suppressAutoApplyOnHide = true;
    this.popover?.hide();
  }

  onPopoverHide(): void {
    if (this.suppressAutoApplyOnHide) {
      this.suppressAutoApplyOnHide = false;
      return;
    }

    if (this.isRangeExceedingMax) {
      this.syncDraftValues();
      return;
    }

    this.applyDraftRange();
  }

  onPopoverShow(): void {
    this.enableCalendarHeaderButtons();
  }

  onCalendarPanelChanged(): void {
    this.enableCalendarHeaderButtons();
  }

  onDateSelect(selectedDate: Date): void {
    this.activeQuickRangeDays = null;
    this.onCalendarPanelChanged();
    this.draftPeriod = null;

    const [start, end] = this.draftRangeValue;
    if (start && end) {
      this.firstClickDate = null;
      return;
    }

    if (this.firstClickDate && selectedDate < this.firstClickDate) {
      this.draftRangeValue = [selectedDate, this.firstClickDate];
      this.firstClickDate = null;
      return;
    }

    this.firstClickDate = selectedDate;
  }

  private emitCurrentRange(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.dateRangeChange.emit({
      start: new Date(this.startDate),
      end: new Date(this.endDate),
      period: this.selectedPeriod,
    });
  }

  private initializeDefaultDatesIfNeeded(): void {
    if (!this.startDate && !this.endDate) {
      this.endDate = new Date(this.effectiveMaxDate);
      this.startDate = new Date(this.endDate);
      this.startDate.setDate(this.startDate.getDate() - Math.max(this.defaultRangeDays - 1, 0));
      this.selectedPeriod = this.defaultRangeDays;
      this.activeQuickRangeDays = this.defaultRangeDays;
    } else if (this.startDate && !this.endDate) {
      this.endDate = new Date(this.effectiveMaxDate);
      this.selectedPeriod = null;
    } else if (!this.startDate && this.endDate) {
      this.startDate = new Date(this.endDate);
      this.startDate.setDate(this.startDate.getDate() - Math.max(this.defaultRangeDays - 1, 0));
      this.selectedPeriod = null;
    }

    if (this.startDate && this.startDate < this.minDate) {
      this.startDate = new Date(this.minDate);
    }

    if (this.endDate && this.endDate > this.effectiveMaxDate) {
      this.endDate = new Date(this.effectiveMaxDate);
    }

    this.startDate?.setHours(0, 0, 0, 0);
    this.endDate?.setHours(23, 59, 0, 0);

    this.syncDraftValues();
    this.updateDisplayRangeLabel();
  }

  private syncDraftValues(): void {
    if (!this.startDate || !this.endDate) {
      this.draftRangeValue = [];
      return;
    }

    this.draftRangeValue = [new Date(this.startDate), new Date(this.endDate)];
    this.draftPeriod = this.selectedPeriod;
  }

  private updateDisplayRangeLabel(): void {
    this.displayRangeLabel = `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`;
  }

  private formatDate(value: Date | null): string {
    if (!value) {
      return '-';
    }

    const datePart = this.displayDateFormatter.format(value);
    const hour = value.getHours().toString().padStart(2, '0');
    const minute = value.getMinutes().toString().padStart(2, '0');
    return `${datePart}, ${hour}:${minute}`;
  }

  private refreshEffectiveMaxDate(): void {
    this.effectiveMaxDate = this.isMaxDateToday ? this.tenantNow() : new Date(this.maxDate);
  }

  /**
   * "Now", expressed as a `Date` whose local (browser) fields carry the tenant's current wall clock —
   * consistent with how `AppComponent`'s outgoing `Date.prototype.toJSON` override re-interprets a
   * range's local fields as tenant time when it is sent to the backend. Deriving "today" from the
   * browser's own `new Date()` here would let the quick-range/default window use the browser's calendar
   * day instead of the tenant's, silently shifting the boundary by up to a day whenever the two zones
   * diverge (e.g. a dev machine outside the tenant's configured `Abp.Timing.TimeZone`).
   */
  private tenantNow(): Date {
    const zone = this.timezoneService.timezone;
    const now = new Date();
    if (!zone) {
      return now;
    }

    const tenantNow = this.timeService.toZone(now, zone);
    return new Date(
      tenantNow.year,
      tenantNow.month - 1,
      tenantNow.day,
      tenantNow.hour,
      tenantNow.minute,
      tenantNow.second,
      tenantNow.millisecond,
    );
  }

  private enableCalendarHeaderButtons(): void {
    if (typeof document === 'undefined') {
      return;
    }

    setTimeout(() => {
      document
        .querySelectorAll(
          '.cx-range-popover .p-datepicker-select-month[disabled], .cx-range-popover .p-datepicker-select-year[disabled]',
        )
        .forEach(button => button.removeAttribute('disabled'));
    }, 0);
  }

  private updateLocalizedLabels(): void {
    this.quickRangeLabels = {
      today: this.localization.instant('Dashboard::Today'),
      last7: this.localization.instant('Dashboard::Last7Days'),
      last30: this.localization.instant('Dashboard::Last30Days'),
      last90: this.localization.instant('Dashboard::Last90Days'),
    };
  }

  private createDisplayDateFormatter(language: string | undefined): Intl.DateTimeFormat {
    const locale = language?.toLowerCase().startsWith('tr') ? 'tr-TR' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  private getRangeFromDraft(): DateRange | null {
    if (this.draftRangeValue.length < 2) {
      return null;
    }

    const start = this.toValidDate(this.draftRangeValue[0]);
    const end = this.toValidDate(this.draftRangeValue[1]);
    if (!start || !end || start > end) {
      return null;
    }

    return { start, end };
  }

  private applyDraftRange(): boolean {
    const range = this.getRangeFromDraft();
    if (!range) {
      return false;
    }

    range.start.setHours(0, 0, 0, 0);
    range.end.setHours(23, 59, 0, 0);
    const hasChanged =
      !this.startDate ||
      !this.endDate ||
      this.startDate.getTime() !== range.start.getTime() ||
      this.endDate.getTime() !== range.end.getTime();

    this.startDate = range.start;
    this.endDate = range.end;
    this.selectedPeriod = this.draftPeriod;
    this.syncDraftValues();
    this.updateDisplayRangeLabel();

    if (hasChanged) {
      this.emitCurrentRange();
    }

    return true;
  }

  private toValidDate(value: Date | undefined): Date | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(value);
  }

  private getDefaultMinDate(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 5000);
    return d;
  }
}
